import { useCallback, useEffect, useMemo, useState } from "react";
import api from "../../../services/api";
import { attachDerivedStatus } from "../utils/deriveDoseStatus";

const STATUS = {
  ATRASADA: "ATRASADA",
  PENDENTE: "PENDENTE",
  PROGRAMADA: "PROGRAMADA",
};

const ONE_HOUR_MS = 60 * 60 * 1000;

const getId = (item) => item?.id ?? item?._id;

const safeNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

const formatPtNumber = (value, digits = 2) => {
  if (value === null || value === undefined) return "";

  const num = Number(value);
  if (!Number.isFinite(num)) return "";

  try {
    return new Intl.NumberFormat("pt-BR", {
      minimumFractionDigits: 0,
      maximumFractionDigits: digits,
    }).format(num);
  } catch {
    return String(Number(num.toFixed(digits))).replace(".", ",");
  }
};

const formatDateTimeBR = (value, scheduleTime) => {
  if (!value) return "—";

  let dateObj = null;
  const str = String(value).trim();

  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) {
    const [y, m, d] = str.split("-").map(Number);
    const timeParts = scheduleTime ? String(scheduleTime).split(":") : ["21", "00"];
    const hh = Number(timeParts[0]) || 21;
    const mm = Number(timeParts[1]) || 0;
    dateObj = new Date(y, m - 1, d, hh, mm);
  } else {
    dateObj = new Date(str);
  }

  if (!dateObj || Number.isNaN(dateObj.getTime())) return "—";

  try {
    const dateFormatted = new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(dateObj);

    let timeFormatted = "";
    if (scheduleTime && String(scheduleTime).trim()) {
      timeFormatted = String(scheduleTime).trim().slice(0, 5);
    } else {
      const hh = String(dateObj.getHours()).padStart(2, "0");
      const mm = String(dateObj.getMinutes()).padStart(2, "0");
      timeFormatted = `${hh}:${mm}`;
    }

    return `${dateFormatted}, ${timeFormatted}`;
  } catch {
    return "—";
  }
};

const toInputDateTimeLocal = (isoOrDate) => {
  const date = isoOrDate ? new Date(isoOrDate) : new Date();
  if (Number.isNaN(date.getTime())) return "";

  const pad = (n) => String(n).padStart(2, "0");

  const yyyy = date.getFullYear();
  const mm = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const hh = pad(date.getHours());
  const mi = pad(date.getMinutes());

  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
};

const pad2 = (n) => String(n).padStart(2, "0");

const todayInputDate = () => {
  const date = new Date();
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(
    date.getDate()
  )}`;
};

const nowInputTime = () => {
  const date = new Date();
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
};

const isValidHHMM = (value) => {
  if (!value || typeof value !== "string") return false;
  return /^([01]\d|2[0-3]):([0-5]\d)$/.test(value);
};

const readLastDoseTime = () => {
  try {
    const raw = localStorage.getItem("lastDoseTime");
    if (isValidHHMM(raw)) return raw;
  } catch {
    // sem ação
  }

  return "19:00";
};

const writeLastDoseTime = (time) => {
  if (!isValidHHMM(time)) return;

  try {
    localStorage.setItem("lastDoseTime", time);
  } catch {
    // sem ação
  }
};

const splitISOToDateTimeInputs = (isoOrDate) => {
  const local = toInputDateTimeLocal(isoOrDate);
  if (!local) return { date: "", time: "" };

  const [date, time] = local.split("T");

  return {
    date: date || "",
    time: time || "",
  };
};

const combineDateTimeToISO = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return "";

  const date = new Date(`${dateStr}T${timeStr}`);
  if (Number.isNaN(date.getTime())) return "";

  return date.toISOString();
};

const formatDelay = (dateISO) => {
  if (!dateISO) return "";

  const dateMs = new Date(dateISO).getTime();
  if (Number.isNaN(dateMs)) return "";

  const diffMs = Date.now() - dateMs;
  if (diffMs <= ONE_HOUR_MS) return "";

  const totalMin = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMin / 60);
  const minutes = totalMin % 60;

  if (hours <= 0) return `${totalMin} min atrasada`;
  if (minutes === 0) return `${hours}h atrasada`;

  return `${hours}h ${minutes}min atrasada`;
};

export default function useDosesData() {
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState({ text: "", type: "" });
  const [doses, setDoses] = useState([]);
  const [flasks, setFlasks] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setMessage({ text: "", type: "" });

    try {
      const [dosesRes, flasksRes] = await Promise.all([
        api.get("/doses"),
        api.get("/flasks"),
      ]);

      const dosesList = Array.isArray(dosesRes.data) ? dosesRes.data : [];
      const flasksList = Array.isArray(flasksRes.data) ? flasksRes.data : [];

      setDoses(dosesList);
      setFlasks(flasksList);
    } catch {
      setMessage({ text: "Erro ao carregar dados.", type: "error" });
      setDoses([]);
      setFlasks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const selectableFlasks = useMemo(() => {
    const list = Array.isArray(flasks) ? flasks : [];

    return list.filter(
      (flask) => String(flask?.status || "").toUpperCase() !== "UTILIZADO"
    );
  }, [flasks]);

  const flaskById = useMemo(() => {
    const map = new Map();

    (flasks || []).forEach((flask) => {
      map.set(String(getId(flask)), flask);
    });

    return map;
  }, [flasks]);

  const getDoseFlask = useCallback(
    (dose) => {
      const flaskId = dose?.flaskId ? String(dose.flaskId) : "";
      if (flaskId && flaskById.has(flaskId)) return flaskById.get(flaskId);
      return null;
    },
    [flaskById]
  );

  // Rótulo formatado inteligente de dosagem por tipo de medicação
  const concLabelForFlask = useCallback((flask) => {
    if (!flask) return "—";

    const type = String(flask?.medicationType || "").toUpperCase();
    const nameLower = String(flask?.medicationName || flask?.name || "").toLowerCase();
    const unitLabel = String(flask?.unitLabel || "").toLowerCase();
    const mgPerUnit = safeNumber(flask?.mgPerUnit ?? flask?.mgBase);

    // 1. SUPLEMENTO EM PÓ (PO / Glutamina / Creatina / Whey)
    if (
      type === "PO" ||
      type === "PÓ" ||
      nameLower.includes("glutamina") ||
      nameLower.includes("creatina") ||
      nameLower.includes("whey") ||
      nameLower.includes("colageno")
    ) {
      if (mgPerUnit != null && mgPerUnit > 0) {
        const gVal = mgPerUnit > 100 ? mgPerUnit / 1000 : mgPerUnit;
        return `${formatPtNumber(gVal, 1)} g / dose`;
      }
      return "Suplemento em Pó";
    }

    // 2. COMPRIMIDO OU CÁPSULA
    if (type === "COMPRIMIDO" || type === "CAPSULA") {
      const unitText = type === "CAPSULA" ? "cápsula" : "comprimido";

      if (mgPerUnit != null && mgPerUnit > 0) {
        if (unitLabel === "mcg" || (mgPerUnit < 1 && unitLabel !== "mg" && unitLabel !== "g")) {
          const mcgVal = mgPerUnit < 1 ? Math.round(mgPerUnit * 1000 * 100) / 100 : mgPerUnit;
          return `${formatPtNumber(mcgVal, 2)} mcg / ${unitText}`;
        }
        if (unitLabel === "g") {
          const gVal = mgPerUnit / 1000;
          return `${formatPtNumber(gVal, 2)} g / ${unitText}`;
        }
        return `${formatPtNumber(mgPerUnit, 2)} mg / ${unitText}`;
      }
      return type === "CAPSULA" ? "Cápsula" : "Comprimido";
    }

    // 3. INJETÁVEL (Frasco / Caneta)
    const mgBase = safeNumber(flask?.mgBase);
    const mlBase = safeNumber(flask?.mlBase);
    const mgPerMl = safeNumber(flask?.mgPerMl);

    if (mgBase != null && mlBase != null && mlBase > 0) {
      return `${formatPtNumber(mgBase)} mg / ${formatPtNumber(mlBase)} mL`;
    }

    if (mgPerMl != null && mgPerMl > 0) {
      return `${formatPtNumber(mgPerMl, 2)} mg/mL`;
    }

    return "Injetável";
  }, []);

  const dosesWithDerivedStatus = useMemo(() => {
    return attachDerivedStatus(doses);
  }, [doses]);

  const grouped = useMemo(() => {
    const list = dosesWithDerivedStatus.slice();

    const activeOrder = new Map([
      [STATUS.ATRASADA, 0],
      [STATUS.PENDENTE, 1],
      [STATUS.PROGRAMADA, 2],
    ]);

    const isActive = (status) =>
      activeOrder.has(String(status || "").toUpperCase());

    const active = [];
    const history = [];

    for (const dose of list) {
      const status = String(
        dose?.__derivedStatus || dose?.status || ""
      ).toUpperCase();

      if (isActive(status)) active.push(dose);
      else history.push(dose);
    }

    const dateMs = (dose) => {
      const time = dose?.date ? new Date(dose.date).getTime() : 0;
      return Number.isFinite(time) ? time : 0;
    };

    active.sort((a, b) => {
      const sa = String(a?.__derivedStatus || "").toUpperCase();
      const sb = String(b?.__derivedStatus || "").toUpperCase();

      const oa = activeOrder.get(sa) ?? 9;
      const ob = activeOrder.get(sb) ?? 9;

      if (oa !== ob) return oa - ob;
      return dateMs(a) - dateMs(b);
    });

    history.sort((a, b) => dateMs(b) - dateMs(a));

    return { active, history };
  }, [dosesWithDerivedStatus]);

  return {
    loading,
    message,
    setMessage,
    doses,
    setDoses,
    flasks,
    setFlasks,
    fetchData,
    selectableFlasks,
    getDoseFlask,
    concLabelForFlask,
    grouped,
    safeNumber,
    formatPtNumber,
    formatDateTimeBR,
    todayInputDate,
    nowInputTime,
    isValidHHMM,
    readLastDoseTime,
    writeLastDoseTime,
    splitISOToDateTimeInputs,
    combineDateTimeToISO,
    formatDelay,
    getId,
  };
}